import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';

type FilterOption = { label: string; value?: string | boolean | any };

type FilterConfig = {
  type: string;
  label: string;
  options: FilterOption[];
  readable?: (value: any) => { label: string; color: string };
};

function FilterMenu({
  title,
  options,
  currentValue,
  onSelect,
}: {
  title?: string;
  options: FilterOption[];
  currentValue: string | boolean | undefined | null;
  onSelect: (value?: string | boolean) => void;
}) {
  const hasSelection = currentValue !== undefined && currentValue !== null && currentValue !== '';
  const selectedLabel = options.find(o => o.value === currentValue)?.label;

  return (
    <Popover className="relative">
      <PopoverButton
        className={classNames(
          'inline-flex items-center gap-x-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
          hasSelection
            ? 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
        )}
      >
        <span>{title}</span>
        {hasSelection && selectedLabel ? (
          <span className="max-w-[8rem] truncate font-semibold">: {selectedLabel}</span>
        ) : null}
        <ChevronDownIcon
          className={classNames('h-4 w-4', hasSelection ? 'text-indigo-500' : 'text-gray-400')}
          aria-hidden="true"
        />
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel
          anchor="bottom end"
          className="z-20 w-[32rem] max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-100 bg-white p-2 text-sm leading-6 shadow-xl ring-1 ring-gray-900/5 [--anchor-gap:0.5rem]"
        >
          <div className="max-h-96 overflow-y-auto">
            <div className="grid grid-cols-3 gap-1.5">
              {options.map(option => {
                const isSelected = option.value === currentValue;
                return (
                  <PopoverButton
                    key={`${title}-${option.label}`}
                    className={classNames(
                      'inline-flex items-center justify-between gap-x-1.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors hover:cursor-pointer',
                      isSelected
                        ? 'bg-indigo-600 text-white hover:bg-indigo-600'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    )}
                    onClick={() => onSelect(option.value)}
                  >
                    <span className="truncate">{option.label}</span>
                    {isSelected ? <CheckIcon className="h-4 w-4 flex-none" /> : null}
                  </PopoverButton>
                );
              })}
            </div>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}

export default function FilterPanelNew({
  filters,
  activeFilters,
  refetch,
  onFilterChange,
  filterKey = 'filters',
  showSelectedFilters = true,
}: {
  filters: FilterConfig[];
  activeFilters?: any;
  refetch: (variables: any) => void;
  onFilterChange?: () => void;
  filterKey?: string;
  showSelectedFilters?: boolean;
}) {
  const [, setSearchParams] = useSearchParams();

  const applyFilter = (type: string, value?: string | boolean) => {
    const nextFilters = { ...activeFilters, [type]: value };
    const queryParams: any = [];
    Object.entries(nextFilters).forEach(([key, val]) => {
      queryParams.push([key, val]);
    });
    setSearchParams(queryParams);
    onFilterChange && onFilterChange();
    refetch({ [filterKey]: nextFilters });
  };

  const hasActiveFilters =
    showSelectedFilters && activeFilters && Object.values(activeFilters).some(v => v);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-gray-400">
          <AdjustmentsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
        </div>
        {filters.map(filter => (
          <FilterMenu
            key={filter.type}
            title={filter.label}
            options={filter.options}
            currentValue={activeFilters?.[filter.type]}
            onSelect={value => applyFilter(filter.type, value)}
          />
        ))}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(activeFilters || {}).map(([key, value]: [string, any]) =>
            value || typeof value === 'boolean' ? (
              <span
                key={`active-filter-${key}`}
                className="inline-flex items-center gap-x-1.5 rounded-full bg-indigo-50 px-3 py-1 text-sm text-indigo-700"
              >
                <span className="text-xs text-gray-500">
                  {filters.find(f => f.type === key)?.label
                    ? filters.find(f => f.type === key)?.label
                    : key === 'text'
                    ? 'Search'
                    : 'Filter'}
                  :
                </span>
                <span className="font-medium">
                  {filters.find(f => f.type === key)?.readable?.(value).label ||
                    filters.find(f => f.type === key)?.options.find(o => o.value === value)
                      ?.label ||
                    value}
                </span>
                <button
                  type="button"
                  className="ml-0.5 rounded-full p-0.5 text-indigo-400 transition-colors hover:bg-indigo-100 hover:text-indigo-600"
                  onClick={() => applyFilter(key, undefined)}
                  aria-label={`Remove ${key} filter`}
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </span>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}
